// Drive público do cliente — acessado sem autenticação via /publico/cliente/{hash}.
// Mostra todos os contratos do cliente e os documentos visíveis em cada um.
import { useEffect, useState } from 'react';
import axios from 'axios';
import { API_URL } from '../config';
import { fmtDataBr as _fmtDataBr } from '../utils/dataFmt';

const STATUS_CONTRATO: Record<string, { label: string; cor: string; bg: string }> = {
  planejamento:        { label: 'Em planejamento',     cor: '#7C3AED', bg: '#F3E8FF' },
  kickoff_pendente:    { label: 'Kick-off pendente',   cor: '#B07500', bg: '#FFF8E8' },
  em_execucao:         { label: 'Em execução',         cor: '#0930D0', bg: '#EFF3FF' },
  pausado:             { label: 'Pausado',             cor: '#888',    bg: '#F2F2F4' },
  aguardando_aprovacao:{ label: 'Aguardando aprovação',cor: '#B07500', bg: '#FFF8E8' },
  concluido:           { label: 'Concluído',           cor: '#0a7f4a', bg: '#E8F5EE' },
};

const fmt = (v: number | string | null | undefined) => {
  const n = typeof v === 'number' ? v : parseFloat(v as any);
  if (isNaN(n)) return '—';
  return n.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
};
const fmtData = _fmtDataBr;

export default function PaginaPublicaCliente({ hash }: { hash: string }) {
  const [data, setData] = useState<any>(null);
  const [erro, setErro] = useState('');
  const [loading, setLoading] = useState(true);

  // Reusa o path /publico/proposta/{hash} — backend detecta se o hash é
  // de cliente (drive Op-1) ou de proposta (link comercial original).
  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API_URL}/publico/proposta/${hash}`);
        const d = res.data.body ? JSON.parse(res.data.body) : res.data;
        setData(d);
      } catch (e: any) {
        setErro(e.response?.data?.error || e.response?.data?.erro || 'Link inválido ou revogado');
      } finally { setLoading(false); }
    })();
  }, [hash]);

  async function baixar(documentoId: string) {
    try {
      const res = await axios.post(`${API_URL}/publico/proposta/${hash}`,
        { action: 'baixar', documento_id: documentoId });
      const d = res.data.body ? JSON.parse(res.data.body) : res.data;
      window.open(d.download_url, '_blank');
    } catch (e: any) {
      alert(e.response?.data?.error || 'Erro ao baixar documento');
    }
  }

  if (loading) return <Tela>Carregando...</Tela>;
  if (erro) return <Tela><div style={{ color: '#B91626' }}>⚠ {erro}</div></Tela>;
  if (!data) return null;

  const cli = data.cliente;
  const contratos = data.contratos || [];

  return (
    <div style={{ minHeight: '100vh', background: '#F0F1F5' }}>
      {/* Cabeçalho */}
      <div style={{ background: '#020D7F', color: '#fff', padding: '20px 24px' }}>
        <div style={{ maxWidth: 980, margin: '0 auto' }}>
          <strong style={{ fontSize: 16, display: 'block' }}>Hydrodata Engenharia</strong>
          <div style={{ fontSize: 12, opacity: 0.85, marginTop: 4 }}>
            Drive de documentos · {cli.razao_social}{cli.cnpj && ` · CNPJ ${cli.cnpj}`}
          </div>
        </div>
      </div>

      <div style={{ maxWidth: 980, margin: '0 auto', padding: '20px 24px' }}>
        {contratos.length === 0 ? (
          <div style={{ background: '#fff', padding: 30, borderRadius: 8, textAlign: 'center',
            color: '#888', boxShadow: '0 2px 8px rgba(0,0,0,0.05)' }}>
            Nenhum contrato disponível no momento.<br />
            <span style={{ fontSize: 11 }}>Entre em contato com a Hydrodata se acreditar que isso é um erro.</span>
          </div>
        ) : (
          contratos.map((c: any) => {
            const st = STATUS_CONTRATO[c.status] || STATUS_CONTRATO.em_execucao;
            return (
              <div key={c.id} style={{ background: '#fff', borderRadius: 8, padding: 22,
                marginBottom: 16, boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
                border: '0.5px solid #e0e0e0' }}>
                {/* Cabeçalho do contrato */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8, flexWrap: 'wrap' }}>
                  <span style={{ fontFamily: 'monospace', color: '#020D7F', fontSize: 13, fontWeight: 700 }}>
                    {c.numero}
                  </span>
                  <span style={{ fontSize: 11, padding: '3px 9px', borderRadius: 3, fontWeight: 600,
                    background: st.bg, color: st.cor }}>{st.label}</span>
                </div>
                <h2 style={{ margin: '0 0 6px', fontSize: 17, color: '#222' }}>{c.titulo}</h2>

                {/* Metadados */}
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)',
                  gap: 12, padding: '10px 0', borderBottom: '0.5px solid #f0f0f0',
                  marginBottom: 14 }}>
                  <div>
                    <div style={{ fontSize: 10, color: '#888', textTransform: 'uppercase' }}>Início</div>
                    <div style={{ fontSize: 13, color: '#222' }}>
                      {fmtData(c.data_inicio_real || c.data_inicio_prevista)}
                    </div>
                  </div>
                  <div>
                    <div style={{ fontSize: 10, color: '#888', textTransform: 'uppercase' }}>Término</div>
                    <div style={{ fontSize: 13, color: '#222' }}>
                      {fmtData(c.data_termino_real || c.data_termino_previsto)}
                    </div>
                  </div>
                  <div>
                    <div style={{ fontSize: 10, color: '#888', textTransform: 'uppercase' }}>Valor</div>
                    <div style={{ fontSize: 13, color: '#222' }}>{fmt(c.valor_contratado)}</div>
                  </div>
                  <div>
                    <div style={{ fontSize: 10, color: '#888', textTransform: 'uppercase' }}>Documentos</div>
                    <div style={{ fontSize: 13, color: '#222' }}>{c.documentos?.length || 0}</div>
                  </div>
                </div>

                {/* Lista de documentos */}
                {c.documentos?.length > 0 ? (
                  <div>
                    <h3 style={{ fontSize: 12, color: '#020D7F', textTransform: 'uppercase',
                      letterSpacing: 0.5, margin: '0 0 8px' }}>Documentos disponíveis</h3>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                      {c.documentos.map((d: any) => (
                        <div key={d.id} style={{ display: 'flex', alignItems: 'center', gap: 10,
                          padding: 10, background: '#FAFBFF', borderRadius: 5,
                          border: '0.5px solid #f0f0f0' }}>
                          <span style={{ fontSize: 18 }}>
                            {d.tipo_arquivo === 'pdf' ? '📄' :
                             d.tipo_arquivo === 'kmz' || d.tipo_arquivo === 'kml' ? '🗺️' :
                             d.tipo_arquivo === 'image' ? '🖼️' : '📎'}
                          </span>
                          {/* Nome de documento entregue ao cliente é token
                              contínuo ("RT_Levantamento_Topobatimetrico_Rev02.pdf")
                              e item de flex não encolhe abaixo do próprio
                              conteúdo — o botão Baixar saía da caixa. Página
                              PÚBLICA: baixar é a única ação que o cliente tem. */}
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ fontSize: 12, fontWeight: 500, color: '#222',
                                          overflowWrap: 'anywhere' }}>{d.nome}</div>
                            {d.descricao && (
                              <div style={{ fontSize: 10, color: '#888', marginTop: 2,
                                            overflowWrap: 'anywhere' }}>{d.descricao}</div>
                            )}
                          </div>
                          <span style={{ fontSize: 10, color: '#888', flexShrink: 0 }}>{d.pasta || '—'}</span>
                          <button onClick={() => baixar(d.id)}
                            style={{ padding: '5px 12px', fontSize: 11, fontWeight: 600,
                              border: 'none', borderRadius: 4, background: '#0930D0',
                              color: '#fff', cursor: 'pointer', flexShrink: 0 }}>
                            📥 Baixar
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                ) : (
                  <div style={{ fontSize: 12, color: '#aaa', fontStyle: 'italic' }}>
                    Nenhum documento disponível pra cliente neste contrato ainda.
                  </div>
                )}
              </div>
            );
          })
        )}

        <div style={{ textAlign: 'center', padding: 20, fontSize: 10, color: '#888' }}>
          Acesso protegido por link único · Hydrodata Engenharia<br />
          {data.consultado_em && `Consultado em ${new Date(data.consultado_em).toLocaleString('pt-BR')}`}
        </div>
      </div>
    </div>
  );
}

function Tela({ children }: { children: React.ReactNode }) {
  return (
    <div style={{ minHeight: '100vh', background: '#F0F1F5',
      display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
      <div style={{ background: '#fff', padding: 40, borderRadius: 8,
        maxWidth: 480, width: '100%', textAlign: 'center', fontSize: 13 }}>
        <h2 style={{ color: '#020D7F', marginTop: 0, fontSize: 18 }}>Hydrodata Engenharia</h2>
        {children}
      </div>
    </div>
  );
}
